// api.js — typed client helpers for the Tavern backend
import { io } from "socket.io-client";

export const SERVER = process.env.REACT_APP_SERVER_URL || "http://localhost:22314";

// ── HTTP helpers ──────────────────────────────────────────────────────────────
async function req(method, path, body) {
  const token = localStorage.getItem("tavern_token");
  const res = await fetch(`${SERVER}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

export async function authorizedFetch(path, options = {}) {
  const token = localStorage.getItem('tavern_token');
  return fetch(`${SERVER}${path}`, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
}

export const api = {
  get:    (p)    => req("GET",    p),
  post:   (p, b) => req("POST",   p, b),
  put:    (p, b) => req("PUT",    p, b),
  patch:  (p, b) => req("PATCH",  p, b),
  delete: (p)    => req("DELETE", p),
};

// ── Auth ──────────────────────────────────────────────────────────────────────
export const auth = {
  register:     (b)    => api.post("/auth/register", b),
  login:        (b)    => api.post("/auth/login", b),
  guest:        (display_name) => api.post("/auth/guest", { display_name }),
  me:           ()     => api.get("/auth/me"),
  mfaSetup:     ()     => api.post("/auth/mfa/setup"),
  mfaVerify:    (code) => api.post("/auth/mfa/verify", { code }),
  mfaChallenge: (b)    => api.post("/auth/mfa/challenge", b),
  mfaDisable:   ()     => api.post("/auth/mfa/disable"),
  phoneSend:    (phone)       => api.post("/auth/phone/send", { phone }),
  phoneVerify:  (phone, code) => api.post("/auth/phone/verify", { phone, code }),
};

// ── Tags (admin-only) ─────────────────────────────────────────────────────────
export const tags = {
  list:         ()              => api.get("/api/tags"),
  allUsers:     ()              => api.get("/api/tags/all-users"),
  allServers:   ()              => api.get("/api/tags/all-servers"),
  create:       (name, color)   => api.post("/api/tags", { name, color }),
  update:       (id, data)      => api.patch(`/api/tags/${id}`, data),
  delete:       (id)            => api.delete(`/api/tags/${id}`),
  setUsers:     (id, user_ids)  => api.put(`/api/tags/${id}/users`, { user_ids }),
  setServers:   (id, server_ids) => api.put(`/api/tags/${id}/servers`, { server_ids }),
  myVisibleUsers: ()            => api.get("/api/tags/my-visible-users"),
};

// ── Temp Rooms ────────────────────────────────────────────────────────────────
export const tempRooms = {
  create:   (name)   => api.post("/api/temp-rooms", { name }),
  mine:     ()       => api.get("/api/temp-rooms/mine"),
  lookup:   (code)   => api.get(`/api/temp-rooms/join/${code}`),
  end:      (id)     => api.post(`/api/temp-rooms/${id}/end`, {}),
  messages: (id)     => api.get(`/api/temp-rooms/${id}/messages`),
};

// ── Pushover ──────────────────────────────────────────────────────────────────
export const pushover = {
  get:    ()     => api.get("/api/pushover"),
  save:   (data) => api.patch("/api/pushover", data),
  test:   ()     => api.post("/api/pushover/test", {}),
};

// ── Servers/channels/messages ─────────────────────────────────────────────────
export const tavern = {
  getServers:       ()                => api.get("/api/servers"),
  getVisibleServers:()                => api.get("/api/servers/visible"),
  createServer:   (b)               => api.post("/api/servers", b),
  joinServer:     (invite)          => api.post("/api/servers/join", { invite }),
  updateServer:   (sid, data)       => api.patch(`/api/servers/${sid}`, data),
  deleteServer:   (sid)             => api.delete(`/api/servers/${sid}`),
  getChannels:    (sid)             => api.get(`/api/servers/${sid}/channels`),
  createChannel:  (sid, name, type) => api.post(`/api/servers/${sid}/channels`, { name, type }),
  deleteChannel:  (cid)             => api.delete(`/api/channels/${cid}`),
  getMembers:     (sid)             => api.get(`/api/servers/${sid}/members`),
  kickMember:     (sid, uid)        => api.delete(`/api/servers/${sid}/members/${uid}`),
  banMember:      (sid, uid)        => api.post(`/api/servers/${sid}/bans/${uid}`, {}),
  getBans:        (sid)             => api.get(`/api/servers/${sid}/bans`),
  unban:          (sid, uid)        => api.delete(`/api/servers/${sid}/bans/${uid}`),
  getMessages:    (cid, opts)       => api.get(`/api/channels/${cid}/messages?limit=${opts?.limit || 50}` + (opts?.before ? `&before=${opts.before}` : "")),
  toggleReaction: (mid, emoji)      => api.post(`/api/messages/${mid}/reactions`, { emoji }),
  getTurnCreds:   ()                => api.get("/turn-credentials"),
  adminGetUsers:  ()                => api.get('/api/admin/users'),
  adminDeleteUser:(uid)             => api.delete(`/api/admin/users/${uid}`),
  // Generic fetch wrappers for AdminPanel (returns raw Response for flexibility)
  get:    (path)       => req("GET",    `/api${path}`),
  post:   (path, body) => req("POST",   `/api${path}`, body),
  put:    (path, body) => req("PUT",    `/api${path}`, body),
  delete: (path)       => req("DELETE", `/api${path}`),
};

export const upgrade = {
  capabilities: () => api.get('/api/upgrade/capabilities'),
  search: params => api.get(`/api/upgrade/messages/search?${new URLSearchParams(Object.entries(params).filter(([,v]) => v !== '' && v != null))}`),
  richMessages: cid => api.get(`/api/upgrade/channels/${cid}/messages-rich`),
  thread: id => api.get(`/api/upgrade/messages/${id}/thread`),
  bookmark: id => api.post(`/api/upgrade/messages/${id}/bookmark`, {}),
  unbookmark: id => api.delete(`/api/upgrade/messages/${id}/bookmark`),
  bookmarks: () => api.get('/api/upgrade/bookmarks'),
  pin: (id, reason) => api.post(`/api/upgrade/messages/${id}/pin`, { reason }),
  pins: cid => api.get(`/api/upgrade/channels/${cid}/pins`),
  roles: sid => api.get(`/api/upgrade/servers/${sid}/roles`),
  createRole: (sid, body) => api.post(`/api/upgrade/servers/${sid}/roles`, body),
  updateRole: (sid, id, body) => api.put(`/api/upgrade/servers/${sid}/roles/${id}`, body),
  setMemberRoles: (sid, uid, role_ids, reason) => api.put(`/api/upgrade/servers/${sid}/members/${uid}/roles`, { role_ids, reason }),
  permissions: cid => api.get(`/api/upgrade/channels/${cid}/my-permissions`),
  setChannelPermissions: (cid, rid, body) => api.put(`/api/upgrade/channels/${cid}/permissions/${rid}`, body),
  setChannelPolicy: (cid, body) => api.patch(`/api/upgrade/channels/${cid}/policy`, body),
  audit: sid => api.get(`/api/upgrade/servers/${sid}/audit`),
  notificationSettings: (sid, cid) => api.get(`/api/upgrade/notifications/settings?${new URLSearchParams({ ...(sid ? { server_id: sid } : {}), ...(cid ? { channel_id: cid } : {}) })}`),
  saveNotificationSettings: body => api.put('/api/upgrade/notifications/settings', body),
  inbox: () => api.get('/api/upgrade/notifications/inbox'),
  markRead: ids => api.post('/api/upgrade/notifications/read', { ids }),
  games: q => api.get(`/api/upgrade/games/catalog${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  addGame: body => api.post('/api/upgrade/games/catalog', body),
  gameState: (id, body) => api.put(`/api/upgrade/games/${id}/state`, body),
  launchGame: (id, body) => api.post(`/api/upgrade/games/${id}/launch`, body),
  controllerProfiles: () => api.get('/api/upgrade/games/controller-profiles'),
  saveControllerProfile: (id, body) => api.put(`/api/upgrade/games/controller-profiles/${id}`, body),
  callTopology: cid => api.get(`/api/upgrade/calls/topology/${cid}`),
  async upload(file, channelId, onProgress = () => {}) {
    const session = await api.post('/api/upgrade/uploads', { channel_id: channelId, name: file.name, mime: file.type, size: file.size });
    let offset = session.offset || 0;
    while (offset < file.size) {
      const chunk = file.slice(offset, Math.min(file.size, offset + session.chunk_size));
      const response = await authorizedFetch(`/api/upgrade/uploads/${session.id}/chunk`, { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream', 'X-Upload-Offset': String(offset) }, body: chunk });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Upload failed');
      offset = result.offset; onProgress(offset / file.size);
    }
    return api.post(`/api/upgrade/uploads/${session.id}/complete`, {});
  },
  async downloadAttachment(id, name) {
    const response = await authorizedFetch(`/api/upgrade/attachments/${id}`);
    if (!response.ok) throw new Error('Attachment download failed');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = name || 'attachment'; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

// ── Socket singleton ──────────────────────────────────────────────────────────
let socket = null;

export function getSocket(token) {
  if (socket?.connected) return socket;
  if (socket) socket.disconnect();
  socket = io(SERVER, {
    auth: { token },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionDelay: 1000,
  });
  return socket;
}

export function disconnectSocket() {
  socket?.disconnect();
  socket = null;
}
